import React, { useState, useEffect } from "react";
import { API_BASE_URL } from "../../../config";
import LoadingScreen from "../../common/LoadingScreen";
import { saveLocalSong } from "../../../utils/songStorage";
import { authFetch } from "../../../utils/auth";

function CloseIcon({ size = 18 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
    );
}

function CheckmarkIcon({ size = 16 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
        </svg>
    );
}

function AlertIcon({ size = 16 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
    );
}

function SongReviewModal({ video, langId, userId, onClose, onSongSaved }) {
    const [step, setStep] = useState("loading"); // "loading" | "review" | "saving" | "error"
    const [errorMessage, setErrorMessage] = useState("");
    const [savingStatus, setSavingStatus] = useState("Downloading audio...");
    const [popupNotice, setPopupNotice] = useState(null);

    // Editable metadata
    const [title, setTitle] = useState(video.title || "");
    const [artist, setArtist] = useState(video.channel || "");

    // Extracted lyrics state
    const [lines, setLines] = useState([]);
    const [wordGlossary, setWordGlossary] = useState({});
    const [detectedLanguage, setDetectedLanguage] = useState("");
    const [matchesCourse, setMatchesCourse] = useState(true);

    // Manual lyrics fallback tab
    const [activeTab, setActiveTab] = useState("preview"); // "preview" | "manual"
    const [manualLyricsText, setManualLyricsText] = useState("");
    const [isAligning, setIsAligning] = useState(false);

    // Helper to translate missing line translations concurrently in browser
    const translateMissingLinesInBatches = async (rawLines, targetLangId) => {
        const isoMapping = { 0: "de", 1: "es", 2: "fr", 3: "ru", 4: "pt" };
        const isoCode = isoMapping[parseInt(targetLangId, 10)] || "es";

        // Find lines that have no English translation
        const pendingIndices = [];
        rawLines.forEach((l, idx) => {
            if (!l.english || !l.english.trim() || l.english === l.foreign) {
                pendingIndices.push(idx);
            }
        });

        if (pendingIndices.length === 0) return;

        const BATCH_SIZE = 5;
        let updatedLines = [...rawLines];

        for (let i = 0; i < pendingIndices.length; i += BATCH_SIZE) {
            const batchIndices = pendingIndices.slice(i, i + BATCH_SIZE);
            await Promise.all(
                batchIndices.map(async (idx) => {
                    const text = (rawLines[idx].foreign || rawLines[idx].text || "").trim();
                    if (!text) return;
                    try {
                        const url = `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=${isoCode}|en`;
                        const res = await fetch(url);
                        if (res.ok) {
                            const resJson = await res.json();
                            if (String(resJson.responseStatus) === "200") {
                                const trans = resJson.responseData?.translatedText;
                                if (trans && !trans.startsWith("MYMEMORY WARNING") && !trans.includes("INVALID")) {
                                    updatedLines[idx] = {
                                        ...updatedLines[idx],
                                        english: trans,
                                        translation: trans
                                    };
                                }
                            }
                        }
                    } catch (e) {
                        // Keep foreign as fallback
                    }
                })
            );
            setLines([...updatedLines]);
        }
    };

    // Initial extraction on modal open
    useEffect(() => {
        let isMounted = true;
        setStep("loading");

        authFetch(`${API_BASE_URL}/api/songs/process-preview`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                video_id: video.video_id,
                title: video.title,
                artist: video.channel,
                lang_id: langId
            })
        })
            .then((res) => {
                if (!res.ok) throw new Error("Failed to process song preview");
                return res.json();
            })
            .then((data) => {
                if (!isMounted) return;
                const incomingLines = data.lines || [];
                setLines(incomingLines);
                setWordGlossary(data.word_glossary || {});
                setDetectedLanguage(data.detected_language || "");
                setMatchesCourse(data.matches_course !== false);

                if (!data.has_lyrics || incomingLines.length === 0) {
                    setActiveTab("manual");
                } else if (!data.has_official_subtitles) {
                    // Translate any missing lines in background concurrently
                    translateMissingLinesInBatches(incomingLines, langId);
                }
                setStep("review");
            })
            .catch((err) => {
                if (!isMounted) return;
                console.error("Preview error:", err);
                setErrorMessage("Failed to extract preview details. You can enter lyrics manually.");
                setStep("review");
                setActiveTab("manual");
            });

        return () => {
            isMounted = false;
        };
    }, [video, langId, userId]);

    // Handle manual lyrics alignment & translation
    const handleAlignManualLyrics = () => {
        if (!manualLyricsText.trim()) return;
        setIsAligning(true);

        authFetch(`${API_BASE_URL}/api/songs/align-lyrics`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                lyrics_text: manualLyricsText,
                lang_id: langId,
                duration: video.duration || 180
            })
        })
            .then((res) => res.json())
            .then((data) => {
                if (data.lines && data.lines.length > 0) {
                    setLines(data.lines);
                    setWordGlossary(data.word_glossary || {});
                    setDetectedLanguage(data.detected_language || "");
                    setMatchesCourse(data.matches_course !== false);
                    setActiveTab("preview");
                } else {
                    setPopupNotice("Could not process lines. Please ensure you entered valid lyrics.");
                }
            })
            .catch((err) => {
                console.error("Manual align error:", err);
                setPopupNotice("Failed to align lyrics. Please try again.");
            })
            .finally(() => {
                setIsAligning(false);
            });
    };

    // Save metadata to server & lyrics locally to IndexedDB (streams audio via YouTube IFrame)
    const handleConfirmSave = async () => {
        setStep("saving");
        setSavingStatus("Saving song...");

        try {
            // 1. Register lightweight metadata with server to get canonical song ID
            const metaRes = await authFetch(`${API_BASE_URL}/api/songs/custom`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    lang_id: langId,
                    title: title.trim(),
                    artist: artist.trim(),
                    duration: video.duration || 0,
                    lines_count: lines.length,
                    cover_image: video.thumbnail || "",
                    cover_gradient: "linear-gradient(135deg, #4338ca 0%, #6366f1 100%)",
                    source_video_id: video.video_id
                })
            });

            const metaData = await metaRes.json();
            if (!metaData.success || !metaData.song) {
                throw new Error(metaData.error || "Failed to save song metadata.");
            }

            const canonicalSongId = metaData.song.id;

            const lyricsData = {
                id: canonicalSongId,
                title: title.trim(),
                artist: artist.trim(),
                duration: video.duration || 0,
                cover_image: video.thumbnail || "",
                lines_count: lines.length,
                lines: lines,
                word_glossary: wordGlossary,
                source_video_id: video.video_id
            };

            // 2. Save lyrics JSON locally in IndexedDB under canonicalSongId
            await saveLocalSong(canonicalSongId, null, lyricsData);

            onSongSaved(metaData.song);
        } catch (err) {
            console.error("Save error:", err);
            setErrorMessage(err.message || "Failed to save song.");
            setStep("error");
        }
    };

    return (
        <div className="song-modal-overlay" onClick={onClose}>
            <div className="song-review-modal" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="song-review-modal__header">
                    <h3 className="song-review-modal__title">Add Song</h3>
                    <button className="song-review-modal__close-btn" onClick={onClose}>
                        <CloseIcon size={20} />
                    </button>
                </div>

                {/* Body depending on step */}
                {step === "loading" && (
                    <LoadingScreen
                        inline
                        title="Looking for lyrics..."
                        subtitle="This may take a minute"
                        size={40}
                    />
                )}

                {step === "saving" && (
                    <LoadingScreen
                        inline
                        title={savingStatus}
                        size={40}
                    />
                )}

                {step === "error" && (
                    <div className="song-review-error-view">
                        <div className="song-review-error-badge">
                            <AlertIcon size={24} />
                            <span>Error saving song</span>
                        </div>
                        <p className="song-review-error-msg">{errorMessage}</p>
                        <button className="song-review-btn song-review-btn--secondary" onClick={() => setStep("review")}>
                            Back to Review
                        </button>
                    </div>
                )}

                {step === "review" && (
                    <div className="song-review-body">
                        {/* Track Info Card */}
                        <div className="song-review-track-card">
                            <img
                                src={video.thumbnail}
                                alt={video.title}
                                className="song-review-track-thumb"
                            />
                            <div className="song-review-fields">
                                <div className="song-review-field">
                                    <label>Title</label>
                                    <input
                                        type="text"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        className="song-review-input"
                                        placeholder="Song Title"
                                    />
                                </div>
                                <div className="song-review-field">
                                    <label>Artist / Channel</label>
                                    <input
                                        type="text"
                                        value={artist}
                                        onChange={(e) => setArtist(e.target.value)}
                                        className="song-review-input"
                                        placeholder="Artist"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Language Confirmation Banner */}
                        {detectedLanguage && (
                            <div className={`song-review-lang-banner ${matchesCourse ? "song-review-lang-banner--match" : "song-review-lang-banner--warning"}`}>
                                {matchesCourse ? (
                                    <>
                                        <CheckmarkIcon size={16} />
                                        <span>
                                            Detected Language: <strong>{detectedLanguage}</strong> (Matches current course)
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        <AlertIcon size={16} />
                                        <span>
                                            Detected <strong>{detectedLanguage}</strong> lyrics. Confirm this matches what you want to learn!
                                        </span>
                                    </>
                                )}
                            </div>
                        )}

                        {/* Tabs: Preview vs Manual */}
                        <div className="song-review-tabs">
                            <button
                                className={`song-review-tab-btn ${activeTab === "preview" ? "active" : ""}`}
                                onClick={() => setActiveTab("preview")}
                            >
                                Synced Lyrics ({lines.length})
                            </button>
                            <button
                                className={`song-review-tab-btn ${activeTab === "manual" ? "active" : ""}`}
                                onClick={() => setActiveTab("manual")}
                            >
                                Paste Lyrics
                            </button>
                        </div>

                        {/* Tab Content */}
                        {activeTab === "preview" && (
                            <div className="song-review-lyrics-container">
                                {lines.length === 0 ? (
                                    <div className="song-review-empty-lyrics">
                                        <p>No synchronized lyrics found automatically for this video.</p>
                                        <button
                                            className="song-review-btn song-review-btn--secondary"
                                            onClick={() => setActiveTab("manual")}
                                        >
                                            Paste Lyrics
                                        </button>
                                    </div>
                                ) : (
                                    <div className="song-review-lyrics-table">
                                        {lines.map((l, idx) => (
                                            <div key={idx} className="song-review-line-row">
                                                <span className="song-review-line-time">{l.start_time}</span>
                                                <div className="song-review-line-content">
                                                    <div className="song-review-line-foreign">{l.foreign || l.text}</div>
                                                    <div className="song-review-line-english">{l.english || l.translation}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === "manual" && (
                            <div className="song-review-manual-container">
                                <textarea
                                    className="song-review-textarea"
                                    rows={8}
                                    placeholder="Paste song lyrics in target language here..."
                                    value={manualLyricsText}
                                    onChange={(e) => setManualLyricsText(e.target.value)}
                                />
                                <button
                                    className="song-review-btn song-review-btn--secondary"
                                    onClick={handleAlignManualLyrics}
                                    disabled={isAligning || !manualLyricsText.trim()}
                                >
                                    {isAligning ? "Processing..." : "Process Lyrics"}
                                </button>
                            </div>
                        )}

                        {/* Footer Controls */}
                        <div className="song-review-footer">
                            <button className="song-review-btn song-review-btn--cancel" onClick={onClose}>
                                Cancel
                            </button>
                            <button
                                className="song-review-btn song-review-btn--primary"
                                onClick={handleConfirmSave}
                                disabled={!title.trim()}
                            >
                                <CheckmarkIcon size={16} />
                                <span>Save Song</span>
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* In-app Notice Popup */}
            {popupNotice && (
                <div
                    className="song-modal-overlay"
                    style={{ zIndex: 1100 }}
                    onClick={(e) => {
                        e.stopPropagation();
                        setPopupNotice(null);
                    }}
                >
                    <div className="song-confirm-popup" onClick={(e) => e.stopPropagation()}>
                        <h4 className="song-confirm-popup__title">Notice</h4>
                        <p className="song-confirm-popup__text">{popupNotice}</p>
                        <div className="song-confirm-popup__actions">
                            <button
                                className="song-confirm-popup__btn song-confirm-popup__btn--cancel"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setPopupNotice(null);
                                }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default SongReviewModal;
